// src/modulos/Login/LoginPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import styles from './LoginPage.module.css';

// ROLE BRACKETS (mesmo mapeamento do Layout.js)
const ROLE_BRACKETS = {
    BASE_ONLY: ['Laboratorio', 'Logistica', 'Marketing'],
    LUXURY_ONLY: ['Inspetor'],
    DUAL_ACCESS: ['TI', 'Diretoria (Admin)']
};

// Helper para determinar qual sistema o usuário deve acessar
const getUserSystem = (role) => {
    if (!role) return 'base';

    if (ROLE_BRACKETS.BASE_ONLY.includes(role)) return 'base';
    if (ROLE_BRACKETS.LUXURY_ONLY.includes(role)) return 'luxury';
    if (ROLE_BRACKETS.DUAL_ACCESS.includes(role)) {
        // Para dual access, verifica preferência salva ou usa base como padrão
        const savedPreference = localStorage.getItem('erp_system_preference');
        return savedPreference === 'luxury' ? 'luxury' : 'base';
    }
    return 'base';
};

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const { currentUser, login, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser) {
            // Determina para onde redirecionar baseado no role do usuário
            const targetSystem = getUserSystem(currentUser.role);
            const targetPath = targetSystem === 'luxury' ? '/tuv/dashboard' : '/dashboard';

            navigate(targetPath, { replace: true });
        }
    }, [currentUser, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (!email || !password) {
            setErrorMsg('Por favor, preencha o email e a senha.');
            return;
        }

        const result = await login(email, password);

        if (!result.success) {
            setErrorMsg('Erro ao fazer login. Verifique suas credenciais.');
        }
    };

    return (
        <div className={styles.loginContainer}>
            <div className={styles.loginBox}>
                <div className={styles.logoSection}>
                    <div className={styles.logoIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <h2 className={styles.title}>E-MRP</h2>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label htmlFor="email" className={styles.label}>
                            <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            placeholder="seu@email.com"
                            className={styles.input}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="password" className={styles.label}>
                            <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 8H19C20.1 8 21 8.9 21 10V18C21 19.1 20.1 20 19 20H5C3.9 20 3 19.1 3 18V10C3 8.9 3.9 8 5 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M8 8V6C8 3.79 9.79 2 12 2C14.21 2 16 3.79 16 6V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <circle cx="12" cy="14" r="1.5" fill="currentColor" stroke="currentColor" strokeWidth="1.5" />
                                <path d="M12 15.5V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            Senha
                        </label>
                        <input
                            id="password"
                            type="password"
                            placeholder="••••••••"
                            className={styles.input}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className={styles.button}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className={styles.buttonSpinner}></span>
                                Acessando...
                            </>
                        ) : (
                            'Entrar'
                        )}
                    </button>

                    {errorMsg && (
                        <div className={styles.errorContainer}>
                            <svg className={styles.errorIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                                <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            <p className={styles.error}>{errorMsg}</p>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default LoginPage;